import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DashboardLayout } from '@/components/DashboardLayout';
import { useAuth } from '@/context/AuthContext';
import { getSubscriptionPlans, createSubscriptionCheckout } from '@/services/api';
import { toast } from 'sonner';
import { 
  CreditCard, 
  Check, 
  Loader2,
  Sparkles,
  Zap,
  Building
} from 'lucide-react';
import { motion } from 'framer-motion';

const planIcons = {
  starter: Sparkles,
  pro: Zap,
  enterprise: Building
};

export default function SubscriptionPage() {
  const { user, refreshUser } = useAuth();
  const [plans, setPlans] = useState({});
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const data = await getSubscriptionPlans();
      setPlans(data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubscribe = async (planKey) => {
    setProcessingPlan(planKey);
    try {
      const { url } = await createSubscriptionCheckout(planKey);
      window.location.href = url;
    } catch (error) {
      console.error('Failed to create checkout:', error);
      const message = error.response?.data?.detail || 'Erreur lors de la création du paiement';
      toast.error(message);
      setProcessingPlan(null);
    }
  };

  const planOrder = ['starter', 'pro', 'enterprise'];

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="subscription-page">
        {/* Header */}
        <div className="text-center max-w-2xl mx-auto">
          <h1 
            className="text-3xl font-bold text-slate-900"
            style={{ fontFamily: 'Manrope, sans-serif' }}
          >
            Choisissez votre plan
          </h1>
          <p className="text-slate-600 mt-2">
            Débloquez toutes les fonctionnalités pour optimiser votre activité Back Market
          </p>
        </div>

        {/* Current Plan Banner */}
        {user?.subscription_plan && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Card className="border-emerald-200 bg-emerald-50">
              <CardContent className="pt-6">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-lg bg-emerald-500 flex items-center justify-center">
                      <CreditCard className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-emerald-900">Abonnement actif</p>
                      <p className="text-sm text-emerald-700">
                        Plan {user.subscription_plan} - {user.subscription_status}
                      </p>
                    </div>
                  </div>
                  <Badge className="bg-emerald-500 text-white capitalize">
                    {user.subscription_plan}
                  </Badge>
                </div>
              </CardContent>
            </Card>
          </motion.div>
        )}

        {/* Plans Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
          </div>
        ) : (
          <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {planOrder.map((planKey, index) => {
              const plan = plans[planKey];
              if (!plan) return null;
              
              const isPro = planKey === 'pro';
              const isCurrentPlan = user?.subscription_plan === planKey;
              const Icon = planIcons[planKey] || Sparkles;
              
              return (
                <motion.div
                  key={planKey}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3, delay: index * 0.1 }}
                >
                  <Card className={`h-full relative ${isPro ? 'border-emerald-500 border-2 shadow-xl' : 'border-slate-200'} ${isCurrentPlan ? 'ring-2 ring-emerald-500 ring-offset-2' : ''}`}>
                    {isPro && (
                      <div className="absolute -top-3 left-1/2 -translate-x-1/2">
                        <Badge className="bg-emerald-500 text-white">Le plus populaire</Badge>
                      </div>
                    )}
                    {isCurrentPlan && (
                      <div className="absolute -top-3 right-4">
                        <Badge className="bg-blue-500 text-white">Plan actuel</Badge>
                      </div>
                    )}
                    <CardHeader className="text-center pb-2">
                      <div className="flex justify-center mb-4">
                        <div className={`w-12 h-12 rounded-lg ${isPro ? 'bg-emerald-500' : 'bg-slate-100'} flex items-center justify-center`}>
                          <Icon className={`w-6 h-6 ${isPro ? 'text-white' : 'text-slate-600'}`} />
                        </div>
                      </div>
                      <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>{plan.name}</CardTitle>
                      <div className="pt-4">
                        <span className="text-4xl font-bold text-slate-900">{plan.price}€</span>
                        <span className="text-slate-500">/mois</span>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-4">
                      <ul className="space-y-3">
                        {plan.features.map((feature, i) => (
                          <li key={i} className="flex items-center gap-2 text-slate-600">
                            <Check className="w-5 h-5 text-emerald-500 flex-shrink-0" />
                            <span>{feature}</span>
                          </li>
                        ))}
                      </ul>
                      <Button 
                        className={`w-full ${isPro ? 'bg-emerald-500 hover:bg-emerald-600' : ''}`}
                        variant={isPro ? 'default' : 'outline'}
                        onClick={() => handleSubscribe(planKey)}
                        disabled={processingPlan === planKey || isCurrentPlan}
                        data-testid={`subscribe-${planKey}-btn`}
                      >
                        {processingPlan === planKey ? (
                          <>
                            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            Redirection...
                          </>
                        ) : isCurrentPlan ? (
                          'Plan actuel'
                        ) : (
                          `Choisir ${plan.name}`
                        )}
                      </Button>
                    </CardContent>
                  </Card>
                </motion.div>
              );
            })}
          </div>
        )}

        {/* FAQ Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="max-w-2xl mx-auto mt-12"
        >
          <Card className="border-slate-200">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                Questions fréquentes
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <p className="font-medium text-slate-900">Puis-je changer de plan ?</p>
                <p className="text-sm text-slate-600 mt-1">
                  Oui, vous pouvez passer à un plan supérieur à tout moment. La différence sera calculée au prorata.
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-900">Comment fonctionne la facturation ?</p>
                <p className="text-sm text-slate-600 mt-1">
                  Vous êtes facturé mensuellement. Vous pouvez annuler à tout moment.
                </p>
              </div>
              <div>
                <p className="font-medium text-slate-900">Existe-t-il un essai gratuit ?</p>
                <p className="text-sm text-slate-600 mt-1">
                  Vous pouvez tester les fonctionnalités de base sans abonnement. Certaines fonctionnalités avancées nécessitent un plan payant.
                </p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </DashboardLayout>
  );
}
